/**
 * Commercial Gating Policy — TreatX Admin
 * ========================================
 *
 * TREATX-COMMERCIAL-GATING-ADMIN-HARDENING-09B
 *
 * Owner-facing policy management page. Intentionally simple:
 *   - shows the currently active milestone
 *   - lists supported milestones (only Deposit Paid is operational today)
 *   - lets the admin switch milestone with optional reason
 *   - lists the most recent audit-trail entries
 *
 * No redesign. No new shell. Reuses existing TreatX admin styling.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { ShieldCheck, History, ArrowLeft } from 'lucide-react';

export default function CommercialGatingPolicy() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [history, setHistory] = useState([]);
  const [chosen, setChosen] = useState('');
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/commercial-gating');
      setPolicy(res.data.policy);
      setMilestones(res.data.supported_milestones || []);
      setHistory(res.data.history || []);
      setChosen(res.data.policy.current_milestone);
    } catch (err) {
      toast({
        title: 'Failed to load policy',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!chosen || chosen === policy?.current_milestone) {
      toast({ title: 'No change to save' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/commercial-gating', {
        milestone: chosen,
        reason: reason || null,
      });
      toast({
        title: 'Policy updated',
        description: `${res.data.old_milestone} → ${res.data.new_milestone}`,
      });
      setReason('');
      await load();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="commercial-gating-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <button
          onClick={() => navigate('/treatx-admin/dashboard')}
          className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          data-testid="back-to-admin-dashboard"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin Dashboard
        </button>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            <div>
              <h1 className="text-xl font-semibold">External Communication Policy</h1>
              <p className="text-sm text-slate-500">
                Controls when patient ↔ provider direct WhatsApp/SMS contact is unlocked.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-slate-500 text-sm">Loading…</div>
          ) : !policy ? (
            <div className="text-red-600 text-sm">Policy unavailable.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm" data-testid="policy-summary">
                <div>
                  <div className="text-slate-500">Current milestone</div>
                  <div className="font-medium" data-testid="current-milestone">
                    {policy.current_milestone_label}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Mode</div>
                  <Badge className="bg-emerald-100 text-emerald-700">{policy.mode}</Badge>
                </div>
                <div>
                  <div className="text-slate-500">Gated channels</div>
                  <div className="font-medium">{(policy.gated_channels || []).join(', ')}</div>
                </div>
                <div>
                  <div className="text-slate-500">Source</div>
                  <Badge variant="outline">{policy.source}</Badge>
                </div>
                <div>
                  <div className="text-slate-500">Last modified</div>
                  <div className="font-medium text-xs">
                    {policy.last_modified_at || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Last modified by</div>
                  <div className="font-medium text-xs">
                    {policy.last_modified_by || '—'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold mb-3">Supported milestones</h2>
          <div className="space-y-2" data-testid="milestone-list">
            {milestones.map((m) => (
              <label
                key={m.id}
                className={`flex items-start gap-3 p-3 rounded border cursor-pointer ${
                  chosen === m.id
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-slate-200 hover:bg-slate-50'
                } ${!m.operational ? 'opacity-60' : ''}`}
                data-testid={`milestone-${m.id}`}
              >
                <input
                  type="radio"
                  name="milestone"
                  value={m.id}
                  disabled={!m.operational}
                  checked={chosen === m.id}
                  onChange={(e) => setChosen(e.target.value)}
                  className="mt-1"
                  data-testid={`milestone-radio-${m.id}`}
                />
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {m.label}
                    {m.is_current && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">current</Badge>
                    )}
                    {!m.operational && (
                      <Badge variant="outline" className="text-xs">future-ready</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{m.description}</div>
                </div>
              </label>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you changing the milestone?"
              data-testid="reason-textarea"
              className="min-h-[60px]"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !chosen || chosen === policy?.current_milestone}
              data-testid="save-policy-button"
            >
              {saving ? 'Saving…' : 'Save policy'}
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold">Recent changes</h2>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="history-empty">
              No changes recorded yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="history-list">
              {history.map((h, i) => (
                <div
                  key={i}
                  className="text-xs border-l-2 border-slate-200 pl-3 py-1"
                  data-testid={`history-row-${i}`}
                >
                  <div>
                    <span className="font-mono">{h.changed_at}</span> ·{' '}
                    <span className="font-medium">{h.action}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">{h.old_value}</span> →{' '}
                    <span className="font-medium">{h.new_value}</span>
                  </div>
                  <div className="text-slate-500">
                    by {h.actor} ({h.actor_role || '—'})
                    {h.reason ? ` — ${h.reason}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
